import { useState } from "react";

import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import {
  CircleAlertIcon,
  CircleCheckIcon,
  ClockIcon,
  SendIcon,
} from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import { Pagination } from "simplydo/react/ui/pagination";
import { Popup } from "simplydo/react/ui/popup";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { EmailLogModal } from "components/lib/EmailLogModal";
import { EmptyBox } from "components/lib/UI";

import type { IdeaPreviewIdea } from "../../types";
import IdeaEmailModal from "./IdeaEmailModal";

type EmailManagementProps = { idea: IdeaPreviewIdea };

const EmailManagement = ({ idea }: EmailManagementProps) => {
  const [page, setPage] = useState(1);
  const { t } = useTranslation();
  const logsQuery = useQuery(
    initUseQuery(api.ideas.getEmailLogs, [idea?._id, page]),
  );
  const [isLoadingLog, setIsLoadingLog] = useState("");
  const [activeLog, setActiveLog] = useState<
    OpenAPI.Schemas["EmailLog"] | null
  >(null);
  const [sendEmailModalOpen, setSendEmailModalOpen] = useState(false);
  const user = useAppSelector((state) => state.user);
  const challengeId = idea?.challenge;

  const canViewProjectEmails = util.hasPermission(
    user!,
    "challenge.viewProjectEmails",
    challengeId,
  );
  const canSendProjectEmails = util.hasPermission(
    user!,
    "challenge.sendProjectEmails",
    challengeId,
  );
  const canManage = util.canManageChallenge(user!, idea?.ownerChallenge);
  if (!canViewProjectEmails && !canSendProjectEmails && !canManage) {
    return null;
  }
  return (
    <div>
      <EmailLogModal log={activeLog} onClose={() => setActiveLog(null)} />
      <IdeaEmailModal
        idea={idea}
        isOpen={sendEmailModalOpen}
        setOpen={setSendEmailModalOpen}
      />
      {(canSendProjectEmails || canManage) && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={() => setSendEmailModalOpen(true)}>
            <Icon icon={SendIcon} />
            Send new email
          </Button>
        </div>
      )}
      {(canViewProjectEmails || canManage) && (
        <>
          {logsQuery.isLoading ? (
            <Loader />
          ) : !logsQuery?.data?.logs?.length ? (
            <EmptyBox>
              No email communication has been sent regarding this{" "}
              {t("generic.idea")}.
            </EmptyBox>
          ) : (
            <>
              <Table size="small">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>Recipient</TableHeaderCell>
                    <TableHeaderCell>Date</TableHeaderCell>
                    <TableHeaderCell>Subject</TableHeaderCell>
                    <TableHeaderCell>Status</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logsQuery?.data?.logs.map((log) => (
                    <TableRow key={log._id}>
                      <TableCell>
                        {log.ownerUser ? (
                          <UserChip user={log.ownerUser} compact="very" />
                        ) : (
                          log.recipientEmail
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDate(log.sentAt || log.createdAt, "dd/MM/yy")}
                      </TableCell>

                      <TableCell>
                        {log.subject.slice(0, 23)}
                        {log.subject.length > 22 && "..."}
                      </TableCell>

                      <TableCell collapsing>
                        {!log.sent ? (
                          <Popup
                            trigger={<Icon icon={ClockIcon} color="yellow" />}
                          >
                            {<span>Awaiting delivery</span>}
                          </Popup>
                        ) : log.sent &&
                          (log.events?.delivered || log.events?.opened) ? (
                          <Popup
                            trigger={
                              <Icon icon={CircleCheckIcon} color="green" />
                            }
                          >
                            {
                              <span>
                                {(log.events?.opened && "Opened") ||
                                  (log.events?.delivered && "Delivered")}
                              </span>
                            }
                          </Popup>
                        ) : log.sent &&
                          (log.events?.bounced || log.events?.dropped) ? (
                          <Popup
                            trigger={
                              <span>
                                <Icon icon={CircleAlertIcon} color="red" />
                              </span>
                            }
                          >
                            {
                              <>
                                <p>{`Status: ${log.events?.bounced ? "Bounced" : "Undeliverable"} `}</p>
                                <p>
                                  We were unable to deliver this message. Please
                                  double check the recipient&apos;s email
                                  address and try again. If this issue persists
                                  please contact us.
                                </p>
                              </>
                            }
                          </Popup>
                        ) : (
                          <Popup
                            trigger={
                              <span>
                                <Icon icon={CircleCheckIcon} color="green" />
                              </span>
                            }
                          >
                            {
                              <>
                                <p>Status: Sent </p>
                                <p>
                                  This email was sent but we have not received
                                  any further delivery information.
                                </p>
                              </>
                            }
                          </Popup>
                        )}
                      </TableCell>

                      <TableCell collapsing>
                        <Button
                          loading={isLoadingLog === log._id}
                          onClick={() => {
                            setIsLoadingLog(log._id);
                            api.ideas.getEmailLog(
                              idea?._id,
                              log._id,
                              (response) => {
                                setIsLoadingLog("");
                                setActiveLog(response.log);
                              },
                              () => {
                                setIsLoadingLog("");
                              },
                            );
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                activePage={page}
                ellipsisItem={null}
                firstItem={null}
                lastItem={null}
                totalPages={logsQuery?.data?.totalPages || 1}
                onPageChange={(_e, { activePage }) => {
                  setPage(activePage as number);
                }}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default EmailManagement;
